/**
 * IssueSheet, bottom sheet that lists quick issue options.
 *
 * The v2 reflex path: when a visitor taps "Something's off", a sheet slides
 * up with a short list of known-common issues. Picking one fires
 * onSubmit(option, attachedImage?) which the parent translates to a
 * POST /api/feedback with `mode: 'issue'` (and uploads the photo first
 * via the /api/feedback/upload-url presigned flow if one is attached).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronRight, Camera, Check } from 'lucide-react';
import { cn } from '@tendedloop/ui';
import { PhotoCapture } from './PhotoCapture';

const CONFIRM_MS = 700;

export interface IssueOption {
  key: string;
  icon: string; // Emoji (e.g. "🧻")
  label: string;
  /** Short hint the backend receives as `issueHint`. */
  hint: string;
}

interface IssueSheetProps {
  open: boolean;
  options: IssueOption[];
  /** Parent-owned attached image. Sheet mutates via onImageChange. */
  attachedImage: File | null;
  onImageChange: (file: File | null) => void;
  /** When true, photo capture is pre-expanded (e.g. opened from the ask-card camera). */
  photoPrompted?: boolean;
  onClose: () => void;
  onSubmit: (option: IssueOption, image: File | null) => void;
  className?: string;
}

export function IssueSheet({
  open,
  options,
  attachedImage,
  onImageChange,
  photoPrompted = false,
  onClose,
  onSubmit,
  className,
}: IssueSheetProps) {
  const { t } = useTranslation('visitor');
  const [confirmingKey, setConfirmingKey] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  // Reset the confirmation state whenever the sheet is closed so a re-open
  // starts clean.
  useEffect(() => {
    if (!open) setConfirmingKey(null);
  }, [open]);

  const handlePick = (opt: IssueOption) => {
    if (confirmingKey) return;
    setConfirmingKey(opt.key);
    onSubmit(opt, attachedImage);
    // Give the visitor ~700ms of in-sheet "Logged ✓" reward before dismissing,
    // matching the AllGood button reward pattern. Parent still handles the
    // actual submit asynchronously; error toasts surface on failure.
    setTimeout(() => onClose(), CONFIRM_MS);
  };

  return (
    <>
      {/* Scrim */}
      <button
        type="button"
        aria-hidden={!open}
        tabIndex={-1}
        onClick={onClose}
        className={cn(
          'fixed inset-0 z-40 bg-black/55 transition-opacity',
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
      />

      {/* Sheet — chrome reads from direction tokens. Atelier renders postcard
           hairlines on cream; Concierge renders Wallet-soft rounded white. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="issue-sheet-title"
        aria-hidden={!open}
        className={cn(
          'fixed inset-x-0 bottom-0 z-50 max-h-[85dvh] overflow-y-auto overscroll-contain rounded-t-[32px] px-5 pt-[18px]',
          'pb-[calc(env(safe-area-inset-bottom)+2rem)]',
          'transition-transform duration-300 ease-out',
          'shadow-[0_-20px_60px_rgba(10,14,20,0.2)]',
          open ? 'translate-y-0' : 'translate-y-full',
          className,
        )}
        style={{ background: 'var(--dir-bg-paper, #fff)' }}
      >
        <div
          aria-hidden="true"
          className="mx-auto mb-4 h-1 w-10 rounded-full"
          style={{ background: 'color-mix(in srgb, var(--dir-ink-muted, #5A6472) 30%, transparent)' }}
        />

        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3
              id="issue-sheet-title"
              className="text-[22px] font-bold leading-tight tracking-[-0.02em]"
              style={{
                color: 'var(--dir-ink-primary)',
                fontFamily: 'var(--dir-font-display)',
                fontStyle: 'var(--dir-display-italic)' as React.CSSProperties['fontStyle'],
              }}
            >
              {t('v2.issueSheet.title', "What's off?")}
            </h3>
            <p
              className="mt-1 text-[15px] font-medium"
              style={{ color: 'var(--dir-ink-muted)', fontFamily: 'var(--dir-font-body)' }}
            >
              {t('v2.issueSheet.hint', "Pick the closest. We'll route it.")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('v2.close', 'Close')}
            className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full transition-all active:scale-95"
            style={{
              background: 'color-mix(in srgb, var(--dir-ink-muted) 12%, var(--dir-bg-paper))',
              color: 'var(--dir-ink-primary)',
            }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Photo attach, either a pill CTA, or the full PhotoCapture if
            the visitor asked to attach a picture. Lives ABOVE the issue list so
            the attached photo rides along with whichever option is picked. */}
        {photoPrompted || attachedImage ? (
          <div
            className="mb-3 rounded-[16px] p-3"
            style={{
              border: 'var(--dir-card-border)',
              background: 'color-mix(in srgb, var(--dir-bg-canvas) 60%, var(--dir-bg-paper))',
            }}
          >
            <PhotoCapture
              capturedImage={attachedImage}
              onCapture={(file) => onImageChange(file)}
              onRemove={() => onImageChange(null)}
            />
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          {options.map((opt) => {
            const isConfirming = confirmingKey === opt.key;
            const otherConfirming = !!confirmingKey && !isConfirming;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => handlePick(opt)}
                disabled={!!confirmingKey}
                aria-live={isConfirming ? 'polite' : undefined}
                className={cn(
                  'flex items-center gap-3 rounded-2xl border p-[14px_16px] text-start',
                  'transition-all duration-200',
                  !confirmingKey && 'active:scale-[0.985] hover:bg-slate-100',
                  otherConfirming && 'opacity-40',
                )}
                style={
                  isConfirming
                    ? {
                        // Logged tile — accent tinted, direction-aware (gold tint for
                        // Atelier, green tint for Concierge). 12% mix on bg-paper.
                        background: 'color-mix(in srgb, var(--dir-accent-primary) 10%, var(--dir-bg-paper))',
                        borderColor: 'color-mix(in srgb, var(--dir-accent-primary) 40%, transparent)',
                      }
                    : {
                        background: 'color-mix(in srgb, var(--dir-ink-muted) 8%, var(--dir-bg-paper))',
                        borderColor: 'color-mix(in srgb, var(--dir-ink-muted) 14%, var(--dir-bg-paper))',
                      }
                }
              >
                {isConfirming && (
                  <span
                    aria-hidden="true"
                    className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full"
                    style={{ background: 'var(--dir-accent-primary)' }}
                  >
                    <Check className="h-4 w-4" strokeWidth={3} style={{ color: 'var(--dir-accent-on)' }} />
                  </span>
                )}
                <span className="text-[24px]" aria-hidden="true">
                  {opt.icon}
                </span>
                <span
                  className="flex-1 text-[16px] font-semibold"
                  style={{
                    color: isConfirming
                      ? 'color-mix(in srgb, var(--dir-accent-primary) 80%, var(--dir-ink-primary))'
                      : 'var(--dir-ink-primary)',
                    fontFamily: 'var(--dir-font-body)',
                  }}
                >
                  {isConfirming ? (
                    <>
                      <span>{t('v2.issueSheet.logged', 'Logged')}</span>
                      <span className="mx-1.5 opacity-60">·</span>
                      <span>{opt.label}</span>
                    </>
                  ) : (
                    opt.label
                  )}
                </span>
                {!isConfirming && (
                  <ChevronRight
                    className="h-4 w-4 rtl:rotate-180"
                    aria-hidden="true"
                    style={{ color: 'var(--dir-ink-muted)' }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Subtle paperclip to open PhotoCapture when not already visible */}
        {!(photoPrompted || attachedImage) && (
          <button
            type="button"
            onClick={() => onImageChange(null) /* no-op file; real attach happens via PhotoCapture picker */}
            className="mt-3 inline-flex min-h-[48px] items-center gap-2 rounded-full px-3.5 py-3 text-[15px] font-medium transition-colors"
            style={{
              border: 'var(--dir-card-border)',
              background: 'var(--dir-bg-paper)',
              color: 'var(--dir-ink-muted)',
            }}
            onClickCapture={(e) => {
              e.preventDefault();
              // Open a transient file input via a label trick, simpler: wrap PhotoCapture and trigger its input.
              const hidden = document.getElementById('issue-sheet-photo-hidden') as HTMLInputElement | null;
              hidden?.click();
            }}
          >
            <Camera className="h-4 w-4" />
            {t('v2.issueSheet.attachPhoto', 'Attach a photo (optional)')}
          </button>
        )}
        {/* Hidden file input for the subtle-paperclip path. Lets us reuse the same
            compression/validation as PhotoCapture without mounting it full-size. */}
        <input
          id="issue-sheet-photo-hidden"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            // Lazy-import compression to keep it out of the critical bundle
            const { compressImage, isValidImageType, MAX_FILE_SIZE } = await import('@/lib/imageCompression');
            if (!isValidImageType(file) || file.size > MAX_FILE_SIZE) return;
            const compressed = await compressImage(file);
            onImageChange(compressed);
          }}
        />
      </div>
    </>
  );
}
